// src/features/admin/pages/Adminhome.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Users,
  ClipboardList,
  LayoutDashboard,
  Settings,
} from "lucide-react";

import Header from "shared/ui/Header";
import { BASE_URL } from "shared/lib/config";

// Child pages
import Overview from "../components/Overview";
import UserManagement from "../components/UserManagement";
import Assessment from "../components/Assessment";
import AdminSettings from "../components/AdminSettings";

export default function Adminhome() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const store =
        sessionStorage.getItem("idToken") ? sessionStorage : localStorage;
      const saved = store.getItem("adminActiveTab");
      return saved || "overview";
    } catch {
      return "overview";
    }
  });

  const handleChangeTab = (id) => {
    setActiveTab(id);
    try {
      const store =
        sessionStorage.getItem("idToken") ? sessionStorage : localStorage;
      store.setItem("adminActiveTab", id);
    } catch (e) {
      console.warn("Failed to persist adminActiveTab", e);
    }
  };

  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("Loading…");

  /* -------- Store Helper -------- */
  const getStore = () => {
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return localStorage;
  };

  /* -------- Hard Logout -------- */
  const hardLogout = () => {
    ["idToken", "userProfile"].forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
    navigate("/");
  };

  /* -------- Hydrate UI from cache instantly -------- */
  useEffect(() => {
    window.scrollTo(0, 0);
    const store = getStore();
    const cachedProfile = JSON.parse(store?.getItem("userProfile") || "null");
    if (cachedProfile) {
      setProfile(cachedProfile);
      setStatus("");
    }
  }, []);

  /* -------- Verify token & refresh profile -------- */
  useEffect(() => {
    const init = async () => {
      const store = getStore();
      const token = store.getItem("idToken");
      if (!token) return hardLogout();

      try {
        const res = await fetch(`${BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Unauthorized");

        const me = await res.json();
        store.setItem("userProfile", JSON.stringify(me));
        setProfile(me);
        setStatus("");

        if (me.role !== "admin") {
          return navigate("/", { replace: true });
        }
      } catch (err) {
        console.error(err);
        hardLogout();
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------- Tabs meta (for design) -------- */
  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "users", label: "Pengguna", icon: Users },
    { id: "assessment", label: "Hasil Asesmen", icon: ClipboardList },
    { id: "settings", label: "Admin Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50/30">
      {/* ======= Header with Admin Profile ======= */}
      <Header profile={profile} onLogout={hardLogout} title="Admin Dashboard" />

      {/* ======= Main ======= */}
      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Optional status text */}
        {status && <div className="text-gray-500 text-sm">{status}</div>}

        {/* ======= Tabs Navigation (new design) ======= */}
        <nav className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleChangeTab(tab.id)}
                  className={`flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-all flex-1 relative rounded-xl
                    ${
                      isActive
                        ? "text-[#4F7CFF] bg-blue-50/50"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/80"
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#4F7CFF] to-transparent" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* ======= Content Card (matches design) ======= */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm">
          {!status && activeTab === "overview" && <Overview />}
          {!status && activeTab === "users" && <UserManagement />}
          {!status && activeTab === "assessment" && <Assessment />}
          {!status && activeTab === "settings" && <AdminSettings />}
        </div>
      </main>
    </div>
  );
}
